import CompanyTypePage from './CompanyTypePage';
import { useState, useEffect } from 'react';
import { companiesAPI } from '../services/api';
import { getView, setView, hasView } from '../services/viewCache';

const CONFIG = {
  icon: 'pvt-ltd',
  name: 'Private Limited Company',
  shortName: 'Private Limited',
  subtitle: 'Most popular entity for startups and growing businesses · Sec 2(68) · Companies Act 2013',
  tags: ['Limited Liability', 'Perpetual Succession', 'Separate Legal Entity', 'Min 2 Directors', 'Min 2 Shareholders'],
  heroBg: 'linear-gradient(135deg, #f0faf9, #e4f5f3)',
  heroAccent: '#b2dbd7',
  titleColor: '#145f5f',
  subtitleColor: '#2d7a74',
  tagBg: '#d0eeeb',
  tagText: '#145f5f',
  accentColor: '#145f5f',
  // Stats will be dynamically loaded
  stats: [],
  filings: [
    { form: 'AOC-4',    title: 'Financial Statements',    deadline: '30d from AGM',  penalty: '₹100/day',    status: 'pending' },
    { form: 'MGT-7',    title: 'Annual Return',           deadline: '60d from AGM',  penalty: '₹100/day',    status: 'pending' },
    { form: 'ADT-1',    title: 'Auditor Appointment',     deadline: '15d from AGM',  penalty: '1×–12× fee by delay',    status: 'filed' },
    { form: 'DIR-3 KYC',title: 'Director KYC',           deadline: '30 Jun / 3 yrs', penalty: 'DIN blocked',  status: 'filed' },
    { form: 'MGT-14',   title: 'Board Resolutions',       deadline: '30d from res.', penalty: '₹500/day',    status: 'filed' },
  ],
  requirements: [
    { icon: '👥', title: 'Shareholders & Directors', desc: 'Min 2, max 200 shareholders · Min 2, max 15 directors · At least one Indian resident director' },
    { icon: '💼', title: 'Share Capital',            desc: 'No minimum paid-up capital requirement post 2015 amendment' },
    { icon: '📋', title: 'Board Meetings',           desc: 'Minimum 4 board meetings per year with max 120 days gap between two meetings' },
    { icon: '🏛️', title: 'Annual General Meeting',  desc: 'Mandatory AGM within 6 months of financial year end (Sep 30 for FY ending March)' },
    { icon: '📊', title: 'Financial Reporting',      desc: 'Audited financial statements, balance sheet, P&L, notes to accounts — filed annually' },
    { icon: '🔒', title: 'Transfer Restrictions',   desc: 'Share transfer restricted via Articles of Association — not freely transferable' },
  ],
  complianceHealth: [
    { label: 'AOC-4 Filed',      percent: 75, color: '#4a6b3f' },
    { label: 'MGT-7 Filed',      percent: 68, color: '#8a6a1f' },
    { label: 'DIR-3 KYC Done',   percent: 90, color: '#4a6b3f' },
    { label: 'AGM Completed',    percent: 85, color: '#4a6b3f' },
  ],
};

const CACHE_KEY = 'companyType:pvtltd';

export default function PvtLimitedPage() {
  // Stale-while-revalidate: seed the last real config so the stats strip does
  // not reset to empty on revisit (services/viewCache).
  const [dynamicConfig, setDynamicConfig] = useState(() => getView(CACHE_KEY) ?? CONFIG);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await companiesAPI.getPvtLimitedStats();
        if (response.success && response.data) {
          const stats = response.data;
          const dynamicStats = [
            { label: 'Companies in Portfolio', value: stats.totalCompanies.toString(), color: 'text-[#145f5f]' },
            { label: 'Fully Compliant',        value: stats.fullyCompliant.toString(), color: 'text-[#4a6b3f]' },
            { label: 'Overdue Filings',        value: stats.overdueFilings.toString(), color: 'text-[#9a564a]' },
            { label: 'Pending Actions',        value: stats.pendingActions.toString(), color: 'text-[#8a6a1f]' },
          ];
          
          const nextConfig = { ...CONFIG, stats: dynamicStats };
          setDynamicConfig(nextConfig);
          setView(CACHE_KEY, nextConfig);
        }
      } catch (error) {
        console.error('Failed to fetch Pvt Limited stats:', error);
        // Keep default stats if API fails
        const defaultStats = [
          { label: 'Companies in Portfolio', value: '0', color: 'text-[#145f5f]' },
          { label: 'Fully Compliant',        value: '0', color: 'text-[#4a6b3f]' },
          { label: 'Overdue Filings',        value: '0', color: 'text-[#9a564a]' },
          { label: 'Pending Actions',        value: '0', color: 'text-[#8a6a1f]' },
        ];
        if (!hasView(CACHE_KEY)) {
          setDynamicConfig(prev => ({
            ...prev,
            stats: defaultStats
          }));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
  }, []);

  if (loading) {
    return <CompanyTypePage config={dynamicConfig} />;
  }

  return <CompanyTypePage config={dynamicConfig} />;
}
